import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import CgTable from 'modules/Common/table';
import CgPanel from 'modules/Common/layout/CgPanel';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import { useCalculateStore } from 'modules/Analytics/stores/useCalculateStore';
import { useBranding } from 'modules/App/hooks/useBranding';
import { applyAccentColor, normalizeHexColor } from 'modules/Common/utils/brandColor';
import {
  computeRowCost as sharedComputeRowCost,
  portalProductRowKey as publicProductRowKey,
} from 'modules/PublicCatalog/utils/costMath';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import formatCnCode from 'modules/Common/utils/formatCnCode';
import PublicCatalogHeader from 'modules/PublicCatalog/components/PublicCatalogHeader';
import CbamCostEstimator from 'modules/PublicCatalog/components/CbamCostEstimator';
import ShareCatalogFilters from 'modules/PublicCatalog/components/ShareCatalogFilters';
import PortalCostCell from 'modules/PublicCatalog/components/PortalCostCell';
import CatalogAssumptionsBar from 'modules/PublicCatalog/components/CatalogAssumptionsBar';
import PortalPasswordGate from 'modules/PublicCatalog/components/PortalPasswordGate';
import { usePublicPortalAccess } from 'modules/PublicCatalog/hooks/useFetchPublicCatalogShare';
import usePortalReportPdf from 'modules/PublicCatalog/hooks/usePortalReportPdf';
import { usePortalCertificatePrices } from 'modules/PublicCatalog/hooks/usePortalCertificatePrices';
import { exportCatalogXlsx } from 'modules/PublicCatalog/utils/catalogExports';
import { resolveInitialCertificatePrice } from 'modules/PublicCatalog/utils/certificatePrice';
import { formatCurrency as formatSharedCurrency, toFixedNonZero } from 'helpers/numberFormat';
import Decimal from 'decimal.js';

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return formatSharedCurrency(value, {
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatEmissions = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return toFixedNonZero(new Decimal(value), 4);
};

const extractYear = (period) => {
  if (period === null || period === undefined) return null;
  const match = String(period).match(/(\d{4})/);
  return match ? match[1] : null;
};

const formatPublishedDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const ErrorState = ({ title, body }) => (
  <div className="flex flex-col items-center justify-center text-center px-6 py-16">
    <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center mb-3">
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        className="text-amber-500"
      />
    </div>
    <h2 className="text-base font-semibold text-slate-900 mb-1">{title}</h2>
    <p className="text-sm text-slate-500 max-w-md">{body}</p>
  </div>
);

// Maps an HTTP status from the meta call or the data POST to a fatal error
// screen. 404 → slug doesn't exist, 410 → operator disabled the portal.
const FATAL_ERROR_COPY = {
  404: {
    title: 'Portal not found',
    body: 'This CBAM portal link is no longer active. Reach out to the supplier who shared it with you for an updated link.',
  },
  410: {
    title: 'Portal disabled',
    body: 'The supplier has turned off this CBAM portal. Contact them directly to request their emissions data.',
  },
};

const getFatalErrorCopy = (status) =>
  FATAL_ERROR_COPY[status] || {
    title: "Couldn't load this portal",
    body: 'Something went wrong loading this portal. Please try again in a moment.',
  };

// Start unfiltered so the table renders every product returned by the
// endpoint as-is. The filters (year / CN code / installation / product ID)
// stay available but are opt-in — we never hide rows the operator published
// behind a default year selection.
const INITIAL_FILTERS = () => ({
  year: '',
  cnCode: [],
  installation: '',
  productName: '',
});

export default function PublicCatalogSharePage() {
  const { slug } = useParams();
  const { search } = useLocation();
  const { carbonPrice } = useCalculateStore();
  // The assumptions bar stores the price as a raw string while typing; coerce
  // to a number so cost math and the breakdown popover (which formats via
  // Number.isFinite) both see a real number.
  const priceNum = Number(carbonPrice) || 0;
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  // Reset to the first page whenever a filter changes — otherwise the user
  // could land on page 5 of a result set that now has 1 page.
  const handleFiltersChange = (next) => {
    setFilters(next);
    setPage(1);
  };

  // Public page bypasses the authenticated branding hook in ContentWrapper —
  // re-apply CSS variables so accents use the platform brand. Also seed the
  // calc store's certificate price: the declarant owns this figure (via the
  // assumptions bar), so we resolve it from the URL ?price= / localStorage /
  // €72 default rather than an auth-gated endpoint the public route can't hit.
  useEffect(() => {
    useBranding.getState().applyCss();
    useCalculateStore.getState().setCarbonPrice(resolveInitialCertificatePrice());
  }, []);

  const {
    metaQuery: { data: meta, isLoading: isMetaLoading, isError: isMetaError, error: metaError },
    payload: data,
    acceptedPassword,
    passwordError,
    dataMutation,
    submitPassword,
  } = usePublicPortalAccess(slug);

  // The full-report PDF re-presents the portal password on every call — the
  // public gate is stateless, so passing the payload check earlier grants no
  // standing authority (CG-2362).
  const reportPdf = usePortalReportPdf({
    slug,
    password: acceptedPassword,
    operatorName: data?.operator?.name,
  });

  // EEX quarter averages for the cost-assumption dropdown, behind the same gate
  // for the same reason. Held until the payload has arrived: before that the
  // viewer has either not cleared the password or has nothing to price.
  const certificatePrices = usePortalCertificatePrices({
    slug,
    password: acceptedPassword,
    enabled: Boolean(data),
  });

  // D50: theme the whole public page — including portaled dialogs, which render
  // to document.body outside any wrapper — to the supplier's color. An explicit
  // ?primaryColor override wins, else the payload's server-resolved color (their
  // choice, or the deployment/whitelabel brand default). Set both --primary-color
  // and its dark variant at :root so every accent (and portaled dialog) matches,
  // restoring the deployment brand on unmount / when the color changes.
  const supplierColor =
    normalizeHexColor(new URLSearchParams(search).get('primaryColor')) ||
    normalizeHexColor(data?.operator?.primaryColor);
  useEffect(
    () =>
      applyAccentColor(document.documentElement, supplierColor, () =>
        useBranding.getState().applyCss(),
      ),
    [supplierColor],
  );

  const finalProducts = useMemo(
    () => (data?.products || []).filter((p) => p?.isFinal !== false),
    [data?.products],
  );

  const availableYears = useMemo(() => {
    const years = new Set();
    finalProducts.forEach((p) => {
      const y = extractYear(p?.reportingPeriod);
      if (y) years.add(y);
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [finalProducts]);

  const yearOptions = useMemo(
    () => availableYears.map((y) => ({ value: y, label: y })),
    [availableYears],
  );

  const cnCodeOptions = useMemo(() => {
    const seen = new Map();
    finalProducts.forEach((p) => {
      if (p?.cnCode && !seen.has(p.cnCode)) {
        seen.set(p.cnCode, { value: p.cnCode, label: formatCnCode(p.cnCode) });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [finalProducts]);

  const installationOptions = useMemo(() => {
    const seen = new Map();
    finalProducts.forEach((p) => {
      const name = p?.installationName;
      if (name && !seen.has(name)) seen.set(name, { value: name, label: name });
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [finalProducts]);

  const filtered = useMemo(() => {
    const needle = (filters.productName || '').trim().toLowerCase();
    return finalProducts.filter((p) => {
      if (filters.year && extractYear(p?.reportingPeriod) !== filters.year) return false;
      if (filters.cnCode?.length && !filters.cnCode.includes(p?.cnCode)) return false;
      if (filters.installation && p?.installationName !== filters.installation) return false;
      if (needle && !(p?.productName || '').toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [finalProducts, filters]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (safePage - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [filtered, safePage, limit]);
  const pagedValues = useMemo(
    () => paged.map((product, index) => ({ ...product, _id: publicProductRowKey(product, index) })),
    [paged],
  );

  const headers = [
    { label: 'Product', key: 'cnCode' },
    { label: 'Installation', key: 'installation' },
    { label: 'Direct', key: 'direct' },
    { label: 'Indirect', key: 'indirect' },
    { label: 'Total SEE', key: 'total' },
    { label: 'SEFA', key: 'sefa' },
    { label: 'CBAM Cost / t', key: 'premium' },
  ];

  // Shared with the operator preview page — see utils/costMath. The pages must
  // price the same payload identically (D6).
  const computeRowCost = (p) => sharedComputeRowCost(p, priceNum);

  const rows = paged.map((p, i) => {
    const { sefa, cost, base } = computeRowCost(p);
    return {
      cnCode: p?.cnCode ? (
        <div className="flex flex-col gap-1 min-w-0">
          <CgCnGoodHoverText
            cnCode={p.cnCode}
            useAutonomousData
          />
          <div className="text-[10px] text-slate-400">
            {p?.reportingPeriod || '–'}
            {p?.productName ? (
              <>
                <span className="mx-1">·</span>
                <span className="text-slate-500">{p.productName}</span>
              </>
            ) : null}
          </div>
          {p?.revisionNumber ? (
            <div className="text-[10px] text-slate-400">
              Revision {p.revisionNumber}
              {formatPublishedDate(p?.publishedAt) ? (
                <>
                  <span className="mx-1">·</span>
                  Published {formatPublishedDate(p.publishedAt)}
                </>
              ) : null}
              {p?.methodologyLabel ? (
                <>
                  <span className="mx-1">·</span>
                  {p.methodologyLabel}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        '-'
      ),
      installation: p?.installationName ? (
        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-sm text-slate-700">{p.installationName}</span>
          {(p?.installationCity || p?.installationCountry) && (
            <span className="text-[10px] text-slate-400">
              {[p.installationCity, p.installationCountry].filter(Boolean).join(', ')}
            </span>
          )}
        </div>
      ) : (
        '-'
      ),
      direct: formatEmissions(p?.direct),
      indirect: formatEmissions(p?.indirect),
      total: formatEmissions(p?.total),
      sefa:
        sefa != null ? (
          <span className="text-sm text-gray-700">
            {formatEmissions(sefa)}
            <span className="text-[11px] text-gray-500 ml-1">tCO₂e/t</span>
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
      premium: (
        <PortalCostCell
          cost={cost}
          base={base}
          sefa={sefa}
          isDirectOnly={!!p?.useDirect}
          carbonPrice={priceNum}
          trigger="click"
        />
      ),
      _id: publicProductRowKey(p, i),
    };
  });

  // Returns the promise so the download dialog can show the Excel button busy.
  const handleDownloadExcel = () =>
    exportCatalogXlsx({
      products: filtered,
      computeRow: computeRowCost,
      operator: data?.operator,
      yearTag: filters.year,
      // The CBAM Cost column is the reader's own price assumption applied to the
      // supplier's emissions. Shipping the column without the price makes the
      // spreadsheet unreadable later and authoritative-looking now.
      certificatePrice: carbonPrice,
      quarterPrices: certificatePrices.quarters,
    }).catch((error) => {
      // The exporter loads xlsx on demand now, so a failure arrives as a
      // rejection rather than a throw inside the handler.
      console.error('Failed to export catalog to Excel:', error);
    });

  // ---- Render gates (all hooks above run unconditionally) ----

  if (isMetaLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <CgPageLoader
          ariaLabel="Opening this CBAM portal"
          statusMessages={[
            'Opening this CBAM portal',
            'Checking the link is still active',
            'Loading the supplier’s details',
          ]}
        />
      </div>
    );
  }

  // Fatal states: meta failed (404 missing slug / 410 disabled / other), or the
  // data POST failed with something other than 401 (a 401 is a password miss,
  // handled by the gate below).
  const postStatus = dataMutation.error?.response?.status;
  const isFatal = isMetaError || (dataMutation.isError && postStatus !== 401);
  if (isFatal) {
    const fatalStatus = isMetaError ? metaError?.response?.status : postStatus;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <CgPanel>
          <ErrorState {...getFatalErrorCopy(fatalStatus)} />
        </CgPanel>
      </div>
    );
  }

  // Password required and not yet unlocked → show the gate.
  if (!data && meta?.requiresPassword) {
    return (
      <PortalPasswordGate
        operatorName={meta.operatorName}
        operatorLogoUrl={meta.operatorLogoUrl}
        loading={dataMutation.isPending}
        error={passwordError}
        onSubmit={submitPassword}
      />
    );
  }

  // No password required — the auto-post is in flight.
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <CgPageLoader
          ariaLabel="Loading this supplier’s emissions data"
          statusMessages={[
            'Loading published emissions data',
            'Reading the CN goods on offer',
            'Preparing your CBAM cost estimate',
          ]}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
        <PublicCatalogHeader
          operator={data?.operator}
          onDownloadExcel={handleDownloadExcel}
          reportPdf={reportPdf}
        />

        <div className="mx-4">
          <CatalogAssumptionsBar quarterPrices={certificatePrices.quarters} />
          <ShareCatalogFilters
            values={filters}
            onChange={handleFiltersChange}
            yearOptions={yearOptions}
            cnCodeOptions={cnCodeOptions}
            installationOptions={installationOptions}
          />
          <CgTable
            flat
            tableKey="public-catalog-share"
            headers={headers}
            data={rows}
            values={pagedValues}
            pagination={{
              page: safePage,
              limit,
              total,
              onPageChange: (newPage, newLimit) => {
                if (newLimit) {
                  setPage(1);
                  setLimit(newLimit);
                } else {
                  setPage(newPage);
                }
              },
            }}
            emptyStateVariant="default"
            emptyStateTitle={
              availableYears.length === 0
                ? 'No products published yet'
                : 'No products match these filters'
            }
            emptyStateDescription={
              availableYears.length > 0
                ? 'Try clearing or widening the filters above.'
                : 'This supplier hasn’t published any product emissions yet.'
            }
          />
        </div>

        <CbamCostEstimator
          products={finalProducts}
          operatorName={data?.operator?.name}
        />
      </div>
    </div>
  );
}
